import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from '../i18n';
import { SubGoal } from '../services/goalService';
import ConfirmDialog from './ConfirmDialog';
import Icon from './Icon';
import { getSubGoalProgress } from '../utils/goalProgress';
import { formatPercent } from '../utils/format';
import './SubGoalCard.css';
import { logger } from '../utils/logger';
import { useCompletionToast } from '../hooks/useCompletionFeedback';

interface SubGoalCardProps {
  subGoal: SubGoal;
  goalId: string;
  onUpdateSubGoal: (goalId: string, subGoalId: string, data: Partial<SubGoal>) => Promise<void> | void;
  onDeleteSubGoal: (goalId: string, subGoalId: string) => Promise<void> | void;
  onEditSubGoal?: (subGoal: SubGoal) => void;
  onNavigateToTodos?: (subGoalId: string) => void;
  isLoading?: boolean;
  disableDeleteConfirm?: boolean;
}

const SubGoalCard: React.FC<SubGoalCardProps> = React.memo(({
  subGoal,
  goalId,
  onUpdateSubGoal,
  onDeleteSubGoal,
  onEditSubGoal,
  onNavigateToTodos,
  isLoading = false,
  disableDeleteConfirm = false,
}) => {
  const { t, language } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const showCompletionToast = useCompletionToast();

  const handleNavigateToTodos = useCallback(() => {
    if (onNavigateToTodos) {
      onNavigateToTodos(subGoal.id);
    } else {
      window.location.hash = `/subgoals/${subGoal.id}/todos`;
    }
  }, [onNavigateToTodos, subGoal.id]);

  const handleToggleCompletion = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const completing = !subGoal.completed;
    const subGoalId = subGoal.id;
    try {
      await onUpdateSubGoal(goalId, subGoalId, { completed: completing });
      // T-103 (native T-098): completing gets a toast with Undo; a failed save throws first.
      if (completing) {
        showCompletionToast(t('progress.subGoalCompleted'), () => onUpdateSubGoal(goalId, subGoalId, { completed: false }));
      }
    } catch (error) {
      logger.error('Failed to update subgoal', error);
    }
  }, [goalId, subGoal.id, subGoal.completed, onUpdateSubGoal, showCompletionToast, t]);

  const handleConfirmDelete = useCallback(async () => {
    setShowDeleteConfirm(false);
    try {
      await onDeleteSubGoal(goalId, subGoal.id);
    } catch (error) {
      logger.error('Failed to delete subgoal', error);
    }
  }, [goalId, subGoal.id, onDeleteSubGoal]);

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disableDeleteConfirm) {
      handleConfirmDelete();
    } else {
      setShowDeleteConfirm(true);
    }
  }, [disableDeleteConfirm, handleConfirmDelete]);

  const handleManageTodos = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleNavigateToTodos();
  }, [handleNavigateToTodos]);

  const handleEdit = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onEditSubGoal) {
      onEditSubGoal(subGoal);
    }
  }, [onEditSubGoal, subGoal]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNavigateToTodos();
    }
  }, [handleNavigateToTodos]);

  // Memoized calculations
  const progressData = useMemo(() => {
    const progress = getSubGoalProgress(subGoal);
    return {
      percentage: progress.percent,
      width: progress.percent,
      completedTodos: progress.todosDone,
      pendingTodos: progress.todosTotal - progress.todosDone,
      hasTodos: progress.todosTotal > 0,
      isReady: progress.status === 'readyToComplete',
    };
  }, [subGoal]);

  return (
    <div 
      className={`subgoal-card ${subGoal.completed ? 'completed' : ''} ${isLoading ? 'loading' : ''} clickable`}
      onClick={handleNavigateToTodos}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${t('subgoals.title')}: ${subGoal.title}. ${t('subgoals.card.manageTodos')}.`}
      style={{ cursor: 'pointer' }}
    >
      {isLoading && <div className="loading-overlay">{t('common.loading')}</div>}
      
      <div className="subgoal-card-header">
        <h5 className="subgoal-card-title">{subGoal.title}</h5>
        <div className="subgoal-card-actions">
          <button
            onClick={handleManageTodos}
            className="subgoal-action-btn manage-todos-btn"
            title={t('subgoals.card.manageTodos')}
            disabled={isLoading}
            aria-label={`${t('subgoals.card.manageTodos')}. ${t('subgoals.card.tasksUnit')}: ${subGoal.todos?.length || 0}.`}
          >
            <Icon name="note" className="subgoal-icon" />
            <span className="todo-count">({subGoal.todos?.length || 0})</span>
          </button>
          <button
            onClick={handleToggleCompletion}
            className={`subgoal-action-btn completion-btn ${subGoal.completed ? 'completed' : ''} ${progressData.isReady ? 'is-ready' : ''}`}
            title={subGoal.completed ? t('subgoals.card.reopen') : progressData.isReady ? t('progress.subGoalReadyHint') : t('subgoals.card.complete')}
            disabled={isLoading}
            aria-label={subGoal.completed ? t('subgoals.card.reopen') : t('subgoals.card.complete')}
          >
            <span className="completion-icon">
              <Icon name={subGoal.completed ? 'circleCheck' : 'circle'} />
            </span>
            <span className="completion-text">
              {subGoal.completed ? t('goalCard.actions.doneShort') : t('goalCard.actions.complete')}
            </span>
          </button>
          {onEditSubGoal && (
            <button
              onClick={handleEdit}
              className="subgoal-action-btn edit-btn"
              title={t('subgoals.editSubGoal')}
              disabled={isLoading}
              aria-label={t('subgoals.editSubGoal')}
            >
              <Icon name="edit" />
            </button>
          )}
          <button
            onClick={handleDelete}
            className="subgoal-action-btn delete-btn"
            title={t('subgoals.card.delete')}
            disabled={isLoading}
            aria-label={t('subgoals.card.delete')}
          >
            <Icon name="trash" />
          </button>
        </div>
      </div>

      <div className="subgoal-card-content">
        <div className="subgoal-card-info">
          {subGoal.description && (
            <p className="subgoal-card-description">{subGoal.description}</p>
          )}
          
          <div className="subgoal-card-progress">
            <div className="sg-progress-info">
              <span className="sg-progress-label">{t('subgoals.card.progress')}</span>
            </div>
            <div className="sg-progress-bar" role="progressbar" aria-valuenow={progressData.percentage} aria-valuemin={0} aria-valuemax={100}>
              <div 
                className="sg-progress-fill"
                style={{ width: `${progressData.width}%` }}
              ></div>
            </div>
            <span className="sg-progress-percentage">{formatPercent(progressData.percentage, language)}</span>
            <span className="sg-progress-text">{progressData.completedTodos}/{progressData.completedTodos + progressData.pendingTodos} {t('subgoals.card.tasksUnit')}</span>
          </div>
        </div>

        <div className="subgoal-card-meta">
          {progressData.hasTodos && (
            <div className="subgoal-todos-summary">
              <div className="todos-stats">
                <span className="stat" aria-label={`${progressData.completedTodos} ${t('subgoals.card.completed')} ${t('subgoals.card.tasksUnit')}`}>
                  <Icon name="circleCheck" className="stat-icon" /> {progressData.completedTodos} {t('subgoals.card.completed')}
                </span>
                <span className="stat" aria-label={`${progressData.pendingTodos} ${t('subgoals.card.pending')} ${t('subgoals.card.tasksUnit')}`}>
                  <Icon name="hourglass" className="stat-icon" /> {progressData.pendingTodos} {t('subgoals.card.pending')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('goalCard.confirm.deleteSubGoal.title')}
        message={t('goalCard.confirm.deleteSubGoal.message')}
        confirmText={t('goalCard.confirm.deleteSubGoal.confirm')}
        cancelText={t('common.cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        type="danger"
      />
    </div>
  );
});

SubGoalCard.displayName = 'SubGoalCard';

export default SubGoalCard;
